import axios from "axios";

const RESOLVED_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api";

// Aviso explícito en dev si la baseURL apunta a producción — ayuda a detectar
// builds cacheados / .env mal leído.
if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
  // eslint-disable-next-line no-console
  console.info("[axios] baseURL =", RESOLVED_BASE_URL);
  if (/criskop\.com/i.test(RESOLVED_BASE_URL)) {
    // eslint-disable-next-line no-console
    console.warn(
      "[axios] ⚠ Estás apuntando a producción desde dev. Revisa NEXT_PUBLIC_API_URL en .env y borra .next si es necesario.",
    );
  }
}

const api = axios.create({
  baseURL: RESOLVED_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// ──────────────────────────────────────────────────────────────────────────
// 401 handler global — sesión expirada
// ──────────────────────────────────────────────────────────────────────────
// Cuando el backend rechaza por token expirado/inválido, hacemos signOut de
// NextAuth y redirigimos al login conservando la URL actual como callback.
// Se debouncea para evitar múltiples redirects si varias requests fallan al
// mismo tiempo (típico al cargar un dashboard con varios fetches paralelos).
let unauthorizedHandling = false;

async function handleUnauthorized() {
  if (unauthorizedHandling) return;
  if (typeof window === "undefined") return;
  unauthorizedHandling = true;
  try {
    // Evitar redirect si YA estamos en rutas públicas (login, register, docs).
    const path = window.location.pathname || "";
    const isPublic =
      path.startsWith("/auth/") ||
      path === "/" ||
      path.startsWith("/docs");
    if (isPublic) {
      unauthorizedHandling = false;
      return;
    }
    const callbackUrl = path + (window.location.search || "");
    // Import dinámico para no romper SSR.
    const { signOut } = await import("next-auth/react");
    await signOut({
      redirect: true,
      callbackUrl: `/auth/login?expired=1&callbackUrl=${encodeURIComponent(
        callbackUrl,
      )}`,
    });
  } catch {
    // Fallback duro si signOut falla.
    window.location.href = "/auth/login?expired=1";
  }
}

api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    if (error?.response?.status === 401) {
      void handleUnauthorized();
    }
    return Promise.reject(error);
  },
);

// Función para crear headers con token
const createAuthHeaders = (token) => ({
  Authorization: `Bearer ${token}`
});

// Funciones wrapper para incluir token como argumento
const apiWithAuth = {
  get: (url, token, config = {}) => {
    return api.get(url, {
      ...config,
      headers: {
        ...config.headers,
        ...createAuthHeaders(token)
      }
    });
  },
  
  post: (url, data, token, config = {}) => {
    return api.post(url, data, {
      ...config,
      headers: {
        ...config.headers,
        ...createAuthHeaders(token)
      }
    });
  },
  
  put: (url, data, token, config = {}) => {
    return api.put(url, data, {
      ...config,
      headers: {
        ...config.headers,
        ...createAuthHeaders(token)
      }
    });
  },
  
  delete: (url, token, config = {}) => {
    return api.delete(url, {
      ...config,
      headers: {
        ...config.headers,
        ...createAuthHeaders(token)
      }
    });
  },
  
  patch: (url, data, token, config = {}) => {
    return api.patch(url, data, {
      ...config,
      headers: {
        ...config.headers,
        ...createAuthHeaders(token)
      }
    });
  },

  /** multipart/form-data (p. ej. subida a Cloudinary). Sin Content-Type JSON.
   * Usa axios.post raw — replicamos el handler 401 manualmente porque la
   * instancia `api` setea Content-Type JSON por defecto. */
  postForm: (url, formData, token, config = {}) => {
    return axios
      .post(`${api.defaults.baseURL}${url}`, formData, {
        ...config,
        headers: {
          Authorization: `Bearer ${token}`,
          ...config.headers,
        },
        withCredentials: true,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          void handleUnauthorized();
        }
        throw err;
      });
  },
};

export default api;
export { apiWithAuth };